import { memo, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useNotificationCenter } from "@/contexts/NotificationContext";
import styles from "./Navbar.module.scss";
import {
  BellNavIcon,
  CalendarNavIcon,
  ChatNavIcon,
  ChevronDownIcon,
  HomeNavIcon,
  ProfileNavIcon,
} from "@/components/SVGComponents/MemberIcons";
import LogoutConfirmModal from "@/components/LogoutConfirmModal/LogoutConfirmModal";
import { clear } from "@/store/slices/globalSlice";
import { captalizeEachWord, clearOnLogout, getCurrentUser } from "@/lib/utils";
import { AUTH_ROUTE_PATHS } from "@/pages/Auth/authConfig";
import routes from "@/routes/routes";
import AuthLogo from "@/pages/Auth/components/AuthLogo";
import { openRazorpayCheckout } from "@/lib/razorPay/razorpayCheckout";
import RazorpayService from "@/services/api/razorpay";

export interface SelectOption {
  label: string;
  value: string;
}

const NAV_ITEMS = [
  {
    label: "Home",
    path: routes.privateRoutes.HOME.path,
    icon: HomeNavIcon,
  },
  {
    label: "Chat",
    path: routes.privateRoutes.CHAT.path,
    icon: ChatNavIcon,
  },
  {
    label: "Event",
    path: routes.privateRoutes.EVENT.path,
    icon: CalendarNavIcon,
  },
  {
    label: "Profile",
    path: routes.privateRoutes.PROFILE.path,
    icon: ProfileNavIcon,
  },
] as const;

const Navbar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { notificationCount, isNotificationOpen, openNotifications } =
    useNotificationCenter();
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);

  const user = useMemo(() => getCurrentUser() || {}, []);
  const displayName = captalizeEachWord(
    user?.userName ||
      user?.name ||
      user?.email?.split("@")?.[0]?.replace(/\./g, " ") ||
      "John Smith",
  );
  const initials = displayName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part[0]?.toUpperCase())
    .join("");

  const handleLogout = () => {
    clearOnLogout();
    dispatch(clear());
    navigate(AUTH_ROUTE_PATHS.signIn);
  };

  useEffect(() => {
    const onDocumentClick = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, []);

  return (
    <header className={styles.topBarContainer}>
      <div className={styles.logoBlock} aria-label="BFClogo">
        <AuthLogo className={styles.logo} />
      </div>

      <nav className={styles.navigation}>
        {NAV_ITEMS.map(({ icon: Icon, label, path }) => (
          <NavLink
            key={label}
            className={({ isActive }) =>
              `${styles.navItem} ${isActive ? styles.navItemActive : ""}`
            }
            to={path}
          >
            <Icon className={styles.navIcon} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className={styles.actions} ref={dropdownRef}>
        <button
          aria-label="Notifications"
          aria-expanded={isNotificationOpen}
          aria-haspopup="dialog"
          className={styles.notificationButton}
          onClick={openNotifications}
          type="button"
        >
          <BellNavIcon />
          {notificationCount > 0 ? (
            <span className={styles.badge}>{notificationCount}</span>
          ) : null}
        </button>

        <button
          className={styles.profileButton}
          onClick={() => setIsOpen((previous) => !previous)}
          type="button"
        >
          <span className={styles.avatar}>{initials || "JS"}</span>
          <span className={styles.userName}>{displayName}</span>
          <ChevronDownIcon
            className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`}
          />
        </button>

        {isOpen ? (
          <div className={styles.dropdown}>
            <button
              className={styles.dropdownItem}
              onClick={() => {
                setIsOpen(false);
                navigate(routes.privateRoutes.PROFILE.path);
              }}
              type="button"
            >
              Profile
            </button>
            <button
              className={styles.dropdownItem}
              onClick={() => {
                setIsOpen(false);
                setLogoutModalOpen(true);
              }}
              type="button"
            >
              Logout
            </button>
          </div>
        ) : null}
      </div>

      <LogoutConfirmModal
        closeModal={setLogoutModalOpen}
        modalOpen={logoutModalOpen}
        onConfirm={handleLogout}
      />
    </header>
  );
};

export default memo(Navbar);
